/**
 * El folio del certificado de incapacidad, y hasta dónde podemos comprobarlo.
 *
 * **El portal no verifica nada contra el IMSS, y conviene decirlo aquí.** No existe
 * un camino por el que esta aplicación pueda preguntarle al instituto si un folio
 * es real: el canal del patrón es el IDSE, va con el certificado digital de la
 * empresa y está pensado para sistemas de nómina. Lo que el portal llama «Validada»
 * significa *RR.HH. miró el certificado y lo aceptó*, no *el IMSS lo confirmó*.
 *
 * Así que aquí hay dos niveles y la diferencia entre ellos es deliberada:
 *
 * **Lo que se rechaza** es lo que no puede ser un folio de nada: comillas, símbolos,
 * espacios. En producción hay uno guardado que dice literalmente
 * `3423423we23"#$"#$"#$"#$2`, porque el campo aceptaba cualquier cosa.
 *
 * **Lo que sólo se advierte** es la forma del CITT —dos letras y seis dígitos—.
 * Ese dato viene de documentación de terceros, no de una fuente del IMSS, y los
 * folios reales de esta empresa todavía no existen para contrastarlo. Bloquear con
 * una regla de la que no estoy seguro impediría registrar una incapacidad real por
 * una corazonada; avisar cuesta nada y quien tiene el papel delante decide.
 */

/** Letras, dígitos y guiones. Lo demás no es un folio, es una errata. */
const ACCEPTABLE = /^[A-Za-z0-9-]+$/;

/** La forma del CITT según la documentación pública: `AB123456`. */
const CITT_SHAPE = /^[A-Za-z]{2}\d{6}$/;

/** Tope generoso: el CITT usa ocho caracteres y un folio privado puede ser más largo. */
export const IMSS_FOLIO_MAX_LENGTH = 20;

/** Si el texto puede guardarse. Vacío se acepta: el folio no siempre es obligatorio. */
export const isStorableImssFolio = (raw: string): boolean => {
  const value = raw.trim();
  if (value.length === 0) return true;
  return value.length <= IMSS_FOLIO_MAX_LENGTH && ACCEPTABLE.test(value);
};

/**
 * Si además tiene la forma de un CITT. **Sólo para advertir**, nunca para impedir.
 * Un certificado privado no la tiene y es perfectamente válido.
 */
export const looksLikeCitt = (raw: string): boolean => CITT_SHAPE.test(raw.trim());
